/*
 * Where We Deliver - postcode checker.
 *
 * Answers one question: can we get it there, and when. Reads
 * assets/delivery-zones.json, which is generated from the store's own postcode
 * dataset (the same source the cart's delivery validation trusts), so the
 * answer here and the answer at checkout come from one place.
 *
 * Every clock is computed in the delivering area's own timezone via
 * Intl.DateTimeFormat, not the browser's - a customer in Perth checking a
 * Sydney postcode on Friday night is asking about Saturday, and Brisbane and
 * Perth don't observe daylight saving while Sydney, Melbourne and Adelaide do.
 */
(function () {
  // The asset can be re-injected by InstantClick on SPA navigation.
  if (window.__blDeliveryCheckerLoaded) return;
  window.__blDeliveryCheckerLoaded = true;

  var PHONE = '(02) 7259 8209';
  var PHONE_HREF = 'tel:+61272598209';
  var URGENT_MINUTES = 90;
  var DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Contrast-checked against the tokens in the design handoff, §6.2.
  var PILL = {
    same: ['#d1e0d7', '#2f5a48'],
    next: ['#d1e0d7', '#2f5a48'],
    soft: ['#ffdddd', '#c93150'],
    off: ['#f2f2f2', '#4a4d49']
  };

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Wall-clock time and weekday inside a given IANA timezone.
  function localNow(tz) {
    var parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: tz,
      hour: 'numeric',
      minute: 'numeric',
      weekday: 'short',
      hour12: false
    }).formatToParts(new Date());
    function get(type) {
      for (var i = 0; i < parts.length; i++) if (parts[i].type === type) return parts[i].value;
      return '0';
    }
    return {
      h: parseInt(get('hour'), 10) % 24,
      m: parseInt(get('minute'), 10),
      wd: get('weekday').slice(0, 3)
    };
  }

  function isWeekend(wd) {
    return wd === 'Sat' || wd === 'Sun';
  }

  function tomorrowIsWeekend(wd) {
    var i = DAYS.indexOf(wd);
    return isWeekend(DAYS[(i + 1) % 7]);
  }

  function minutesUntil(hhmm, now) {
    var bits = hhmm.split(':');
    return parseInt(bits[0], 10) * 60 + parseInt(bits[1], 10) - (now.h * 60 + now.m);
  }

  // "2 hours 14 min", never a bare clock time. Under an hour: "14 minutes".
  function formatRemaining(mins) {
    var h = Math.floor(mins / 60);
    var m = mins % 60;
    if (h <= 0) return m + (m === 1 ? ' minute' : ' minutes');
    return h + (h === 1 ? ' hour ' : ' hours ') + m + ' min';
  }

  function money(price) {
    return '$' + price;
  }

  function DeliveryChecker(root) {
    this.root = root;
    this.form = root.querySelector('[data-checker-form]');
    this.input = root.querySelector('[data-checker-input]');
    this.result = root.querySelector('[data-checker-result]');
    this.data = null;
    this.postcode = '';
    this.zoneId = null;
    this.pickerValue = null;
    this.timer = null;
    this.bind();
    this.load();
  }

  DeliveryChecker.prototype.load = function () {
    var self = this;
    var url = this.root.getAttribute('data-zones-url');
    fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (json) {
        self.data = json;
        self.root.removeAttribute('data-loading');
        // Someone may have submitted while the data was still in flight.
        if (self.postcode) self.answer();
      })
      .catch(function () {
        self.data = null;
        self.root.removeAttribute('data-loading');
        if (self.postcode) self.renderUnavailable();
      });
  };

  DeliveryChecker.prototype.bind = function () {
    var self = this;

    // Four digits only, sanitised on every keystroke.
    this.input.addEventListener('input', function () {
      var cleaned = self.input.value.replace(/[^\d]/g, '').slice(0, 4);
      if (cleaned !== self.input.value) self.input.value = cleaned;
    });

    this.form.addEventListener('submit', function (e) {
      e.preventDefault();
      self.postcode = self.input.value.replace(/[^\d]/g, '').slice(0, 4);
      self.zoneId = null;
      self.answer();
    });

    // The suburb picker only exists for postcodes that span two delivery areas.
    this.result.addEventListener('change', function (e) {
      var select = e.target.closest('[data-suburb-picker]');
      if (!select) return;
      self.pickerValue = select.value;
      self.zoneId = select.value.split('|')[0];
      self.answer();
      // The panel is rebuilt around the picker, so put focus back on it.
      var replaced = self.result.querySelector('[data-suburb-picker]');
      if (replaced) replaced.focus();
    });

    window.addEventListener('pagehide', function () {
      self.destroy();
    });
  };

  DeliveryChecker.prototype.destroy = function () {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  };

  // Recompute and repaint. Called on submit and once a second while a clock is
  // on screen, so the countdown and the roll-forward stay honest.
  DeliveryChecker.prototype.answer = function () {
    if (!this.data) return; // still loading; load() will call back
    var view = this.build(this.postcode, this.zoneId);
    this.render(view);

    this.destroy();
    if (view.hasClock && view.live) {
      var self = this;
      this.timer = setInterval(function () {
        // The section can be torn out by an InstantClick navigation.
        if (!document.contains(self.root)) return self.destroy();

        var next = self.build(self.postcode, self.zoneId);
        if (next.hasClock && next.live) {
          // Patch the clock in place. A full repaint every second would steal
          // focus from the suburb picker and reset the CTA the user is aiming at.
          self.tick(next);
        } else {
          // The cut-off has just passed: repaint once, then stop.
          self.render(next);
          self.destroy();
        }
      }, 1000);
    }
  };

  // Per-second update of only what actually changes.
  DeliveryChecker.prototype.tick = function (view) {
    var headline = this.result.querySelector('[data-checker-headline]');
    if (headline && headline.textContent !== view.headline) headline.textContent = view.headline;

    var clock = this.result.querySelector('[data-checker-clock]');
    if (!clock) return;
    var value = clock.querySelector('[data-checker-remaining]');
    if (value && value.textContent !== view.remaining) value.textContent = view.remaining;
    // Compare against the hex we last wrote - reading back style.backgroundColor
    // returns rgb() and would repaint every second.
    if (clock.getAttribute('data-clock-bg') !== view.clockBg) {
      clock.setAttribute('data-clock-bg', view.clockBg);
      clock.style.background = view.clockBg;
      clock.style.color = view.clockFg;
    }
  };

  DeliveryChecker.prototype.build = function (raw, zoneId) {
    var data = this.data;

    if (!/^\d{4}$/.test(raw)) {
      return {
        tier: 'Not a postcode',
        pill: PILL.off,
        where: 'Four digits, no spaces',
        headline:
          "That isn't an Australian postcode - try the four digits of the suburb you're sending to.",
        hasClock: false,
        cta: { label: 'Browse flowers & gifts', href: '/collections/all' }
      };
    }

    var n = parseInt(raw, 10);
    var entry = data.postcodes[raw];
    var options = data.multi[raw];

    if (!entry && !options) {
      // The Northern Territory is the one region we knowingly don't serve; any
      // other absent postcode is a gap in the route list, and says so plainly.
      var isNT = n >= 800 && n <= 999;
      return {
        tier: 'Not served',
        pill: PILL.off,
        where: isNT ? 'Northern Territory' : 'Postcode ' + raw,
        headline: isNT
          ? "We don't deliver to the Northern Territory yet - flowers can't make the trip in a state we'd stand behind."
          : "We don't have a delivery route for this postcode. Check the digits, or talk to us and we'll tell you what's possible.",
        hasClock: false,
        caveat: isNT
          ? 'Sending to someone in the NT? An e-gift card arrives instantly and lets them choose locally. Our Customer Delight team can talk you through the options on ' +
            PHONE +
            '.'
          : 'Our Customer Delight team can check it against the run sheets directly on ' + PHONE + '.',
        cta: isNT
          ? { label: 'Send an e-gift card', href: '/products/gift-card' }
          : { label: 'Call ' + PHONE, href: PHONE_HREF },
        altCta: isNT ? { label: 'Talk to us', href: PHONE_HREF } : null
      };
    }

    // A postcode that straddles two delivery areas needs the suburb before it
    // can be answered honestly.
    var picker = null;
    var chosen;
    if (options) {
      var match = null;
      for (var i = 0; i < options.length; i++) {
        if (options[i][0] === zoneId) match = options[i];
      }
      picker = { postcode: raw, options: options, selected: match ? match[0] : options[0][0] };
      chosen = match || options[0];
    } else {
      chosen = [entry[0], entry[1]];
    }

    var zone = data.zones[chosen[0]];
    var price = chosen[1];
    var view = zone.sameday
      ? this.buildSameDay(zone, price, raw)
      : this.buildNextDay(zone, price, raw);
    view.picker = picker;
    return view;
  };

  DeliveryChecker.prototype.buildSameDay = function (zone, price, raw) {
    var now = localNow(zone.tz);
    var weekendToday = isWeekend(now.wd);
    var todayCut = weekendToday ? zone.weekend : zone.weekday;
    var todayCutLabel = weekendToday ? zone.weekendLabel : zone.weekdayLabel;
    var left = minutesUntil(todayCut, now);

    var earlierWeekend = zone.weekend !== zone.weekday;
    var weekendNote = earlierWeekend
      ? 'Weekend cut-offs in ' +
        zone.label +
        ' are ' +
        zone.weekendLabel +
        ', not ' +
        zone.weekdayLabel +
        '.'
      : null;

    if (left > 0) {
      var urgent = left <= URGENT_MINUTES;
      return {
        tier: 'Same-day available',
        pill: PILL.same,
        where: zone.label + ' · ' + raw,
        headline: urgent
          ? 'There is still time today. ' + formatRemaining(left) + ' until the ' + zone.label + ' cut-off.'
          : 'Yes. Order within the next ' + formatRemaining(left) + ' and it arrives today.',
        hasClock: true,
        live: true,
        cutoff: todayCutLabel,
        cutoffDay: (weekendToday ? 'Weekend' : 'Weekday') + ' cut-off, ' + zone.label,
        remaining: formatRemaining(left),
        clockBg: urgent ? '#ffdddd' : '#d1e0d7',
        clockFg: urgent ? '#c93150' : '#2f5a48',
        zoneLabel: zone.tzLabel + ', local time',
        cost: money(price),
        caveat: weekendNote,
        cta: { label: 'Shop same-day flowers', href: '/collections/all' },
        altCta: { label: 'See all cut-offs', href: '#cutoffs' }
      };
    }

    // Past the cut-off: roll to the next dispatch rather than show a dead
    // clock. Tomorrow may be a different day type, so recompute the cut-off.
    var nextWeekend = tomorrowIsWeekend(now.wd);
    var nextCutLabel = nextWeekend ? zone.weekendLabel : zone.weekdayLabel;
    return {
      tier: 'Same-day closed · next available tomorrow',
      pill: PILL.soft,
      where: zone.label + ' · ' + raw,
      headline:
        "Today's " +
        todayCutLabel +
        ' cut-off for ' +
        zone.label +
        " has passed. Order now and it's on the road tomorrow morning.",
      hasClock: true,
      live: false,
      cutoff: nextCutLabel,
      cutoffDay: 'Tomorrow, ' + zone.label,
      remaining: 'Ordering for tomorrow',
      clockBg: '#fffafa',
      clockFg: '#080000',
      zoneLabel: zone.tzLabel + ', local time',
      cost: money(price),
      caveat: weekendNote
        ? weekendNote + ' You can pick a delivery date at checkout.'
        : 'You can pick the delivery date at checkout, including any day later this week.',
      cta: { label: 'Order for tomorrow', href: '/collections/all' },
      altCta: { label: 'See all cut-offs', href: '#cutoffs' }
    };
  };

  DeliveryChecker.prototype.buildNextDay = function (zone, price, raw) {
    // Next-day orders are cut at the hub that packs them, so the clock is the
    // hub's, not the destination's.
    var now = localNow(zone.tz);
    var cut = isWeekend(now.wd) ? zone.weekend : zone.weekday;
    var cutLabel = isWeekend(now.wd) ? zone.weekendLabel : zone.weekdayLabel;
    var left = minutesUntil(cut, now);
    var live = left > 0;
    var tas = zone.label === 'Tasmania';

    var base = {
      tier: tas ? 'Next-day · gifts only' : 'Next-day',
      pill: tas ? PILL.soft : PILL.next,
      where: zone.label + ' · ' + raw,
      hasClock: true,
      live: live,
      cutoff: cutLabel,
      cutoffDay: zone.tzLabel + ' dispatch, ' + (live ? 'today' : 'tomorrow'),
      remaining: live ? formatRemaining(left) : 'Dispatching tomorrow',
      clockBg: live && left <= URGENT_MINUTES ? '#ffdddd' : live ? '#d1e0d7' : '#fffafa',
      clockFg: live && left <= URGENT_MINUTES ? '#c93150' : live ? '#2f5a48' : '#080000',
      zoneLabel: zone.tzLabel,
      cost: money(price),
      cta: { label: 'Shop next-day flowers', href: '/collections/all' },
      altCta: { label: 'How your flowers travel', href: '#travel' }
    };

    if (tas) {
      base.headline = live
        ? 'Tasmania: gifts yes, flowers no. Order in the next ' +
          formatRemaining(left) +
          ' and it goes out today for next-day delivery.'
        : "Tasmania: gifts yes, flowers no. Today's " +
          cutLabel +
          ' ' +
          zone.tzLabel +
          ' dispatch has closed - order now and it leaves tomorrow.';
      base.caveat =
        'Biosecurity rules stop fresh stems at the Tasmanian border, so we send the non-floral range instead - chocolate, candles, hampers and cards.';
      base.cta = { label: 'Shop gifts for Tasmania', href: '/collections/gifts' };
      base.altCta = { label: 'Read the Tasmania detail', href: '#cutoffs' };
      return base;
    }

    base.headline = live
      ? 'We deliver here next-day. Order in the next ' +
        formatRemaining(left) +
        ' and it leaves the workshop this afternoon.'
      : "We deliver here next-day. Today's " +
        cutLabel +
        ' ' +
        zone.tzLabel +
        ' dispatch has closed - order now and it leaves tomorrow afternoon.';
    base.caveat =
      "Next-day arrangements are built to travel, so the range is narrower than same-day - you'll see what's available for this postcode at checkout.";
    return base;
  };

  DeliveryChecker.prototype.renderUnavailable = function () {
    this.result.hidden = false;
    this.result.innerHTML =
      '<p class="wwd-checker__fallback">We couldn\'t load the delivery routes just now. Our Customer Delight team can check your postcode directly on <a href="' +
      PHONE_HREF +
      '">' +
      PHONE +
      '</a>.</p>';
  };

  DeliveryChecker.prototype.render = function (view) {
    var html = '';

    html +=
      '<div class="wwd-checker__head">' +
      '<span class="wwd-checker__pill" style="background:' +
      view.pill[0] +
      ';color:' +
      view.pill[1] +
      '">' +
      esc(view.tier) +
      '</span>' +
      '<span class="wwd-checker__where">' +
      esc(view.where) +
      '</span>' +
      '</div>';

    html += '<p class="wwd-checker__headline" data-checker-headline>' + esc(view.headline) + '</p>';

    if (view.picker) {
      html += this.renderPicker(view.picker);
    }

    if (view.hasClock) {
      html +=
        '<div class="wwd-facts">' +
        '<div class="wwd-facts__cell">' +
        '<span class="wwd-facts__label">Cut-off</span>' +
        '<span class="wwd-facts__value">' +
        esc(view.cutoff) +
        '</span>' +
        '<span class="wwd-facts__note">' +
        esc(view.cutoffDay) +
        '</span>' +
        '</div>' +
        '<div class="wwd-facts__cell" data-checker-clock data-clock-bg="' +
        view.clockBg +
        '" style="background:' +
        view.clockBg +
        ';color:' +
        view.clockFg +
        '">' +
        '<span class="wwd-facts__label">Time remaining</span>' +
        '<span class="wwd-facts__value" data-checker-remaining>' +
        esc(view.remaining) +
        '</span>' +
        '<span class="wwd-facts__note" style="color:inherit">' +
        esc(view.zoneLabel) +
        '</span>' +
        '</div>' +
        '<div class="wwd-facts__cell">' +
        '<span class="wwd-facts__label">Delivery</span>' +
        '<span class="wwd-facts__value">' +
        esc(view.cost) +
        '</span>' +
        '<a class="wwd-facts__note wwd-link" href="#costs">What the fee buys</a>' +
        '</div>' +
        '</div>';
    }

    if (view.caveat) {
      html += '<p class="wwd-checker__caveat">' + esc(view.caveat) + '</p>';
    }

    html += '<div class="wwd-checker__actions">';
    html +=
      '<a class="wwd-btn wwd-btn--primary" href="' +
      esc(view.cta.href) +
      '">' +
      esc(view.cta.label) +
      '</a>';
    if (view.altCta) {
      html +=
        '<a class="wwd-btn wwd-btn--ghost" href="' +
        esc(view.altCta.href) +
        '">' +
        esc(view.altCta.label) +
        '</a>';
    }
    html += '</div>';

    this.result.hidden = false;
    this.result.innerHTML = html;
    this.announce(view);
  };

  // One clean announcement per answer. The countdown deliberately sits outside
  // the live region - a screen reader reading it every second is unusable.
  DeliveryChecker.prototype.announce = function (view) {
    var region = this.root.querySelector('[data-checker-announce]');
    if (!region) return;
    var parts = [view.tier + '.', view.headline];
    if (view.hasClock) parts.push('Cut-off ' + view.cutoff + ', ' + view.cutoffDay + '. Delivery ' + view.cost + '.');
    if (view.caveat) parts.push(view.caveat);
    region.textContent = parts.join(' ');
  };

  DeliveryChecker.prototype.renderPicker = function (picker) {
    var data = this.data;
    var rows = [];
    for (var i = 0; i < picker.options.length; i++) {
      var opt = picker.options[i];
      for (var j = 0; j < opt[2].length; j++) {
        rows.push({ suburb: opt[2][j], zone: opt[0], value: opt[0] + '|' + opt[2][j] });
      }
    }
    rows.sort(function (a, b) {
      return a.suburb < b.suburb ? -1 : a.suburb > b.suburb ? 1 : 0;
    });

    // Keep the suburb the customer picked selected across re-renders; before
    // they pick, lead with the first suburb of the best-served area.
    var selected = this.pickerValue;
    var known = false;
    for (var m = 0; m < rows.length; m++) if (rows[m].value === selected) known = true;
    if (!known) {
      selected = null;
      for (var p = 0; p < rows.length; p++) {
        if (rows[p].zone === picker.selected) {
          selected = rows[p].value;
          break;
        }
      }
    }

    var html =
      '<div class="wwd-checker__picker">' +
      '<label class="wwd-checker__picker-label" for="wwd-suburb">Postcode ' +
      esc(picker.postcode) +
      ' covers more than one delivery area. Which suburb is it going to?</label>' +
      '<select class="wwd-checker__select" id="wwd-suburb" data-suburb-picker>';
    for (var k = 0; k < rows.length; k++) {
      html +=
        '<option value="' +
        esc(rows[k].value) +
        '"' +
        (rows[k].value === selected ? ' selected' : '') +
        '>' +
        esc(rows[k].suburb) +
        ' - ' +
        esc(data.zones[rows[k].zone].label) +
        '</option>';
    }
    html += '</select></div>';
    return html;
  };

  function init() {
    var roots = document.querySelectorAll('[data-delivery-checker]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__blChecker) continue;
      roots[i].__blChecker = new DeliveryChecker(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
